import { useCallback, useRef } from 'react';
import { AgentService } from '@/services/agent';
import { useAgentStore } from '@/stores/agentStore';
import { createProcessLog, finalizeProcessLogs } from '../helpers';
// 🔵 #17: addActiveProcessLog 统一走 processLogManager，不再本地重复实现
import { addActiveProcessLog as _addProcessLog } from '../processLogManager';
import { ProcessLogKind, ProcessLogStatus, Message } from '../types';
import { thinkingSecondsMap } from '../streamState';
// 🔵 v3-#3: 原子操作需要引用 STREAMING_RESET
import { STREAMING_RESET } from '@/stores/agentStore';

export function useAgentMessages() {
  const lastFinalizedIdRef = useRef<string | null>(null);

  const updateMessage = useCallback((id: string | undefined, updates: Partial<Message>) => {
    if (!id) return;
    useAgentStore.getState().setMessages(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  }, []);

  // 🔵 #17: 代理到 processLogManager 的统一实现，保持类型签名一致
  const addActiveProcessLog = useCallback((
    kind: ProcessLogKind,
    title: string,
    detail?: string,
    status: ProcessLogStatus = 'completed'
  ) => {
    _addProcessLog(kind, title, detail, status);
  }, []);

  const completeLatestActiveProcessLog = useCallback((title?: string, detail?: string) => {
    useAgentStore.getState().updateActiveProcessLogs((prev) => {
      const next = [...prev];
      const activeIndex = next.map((log) => log.status).lastIndexOf('active');
      if (activeIndex === -1) {
        return title ? [...next, createProcessLog('thinking', title, detail, 'completed')] : next;
      }
      next[activeIndex] = {
        ...next[activeIndex],
        status: 'completed',
        title: title ?? next[activeIndex].title,
        detail: detail ?? next[activeIndex].detail,
      };
      return next;
    });
  }, []);

  const failLatestActiveProcessLog = useCallback((title: string, detail?: string) => {
    useAgentStore.getState().updateActiveProcessLogs((prev) => {
      const next = [...prev];
      const activeIndex = next.map((log) => log.status).lastIndexOf('active');
      if (activeIndex === -1) {
        return [...next, createProcessLog('error', title, detail, 'error')];
      }
      next[activeIndex] = {
        ...next[activeIndex],
        kind: 'error',
        status: 'error',
        title,
        detail: detail ?? next[activeIndex].detail,
      };
      return next;
    });
  }, []);

  // 🔵 P2-7: 统一的消息最终化入口，接受任意 Message 字段（searchData / generationOptions / pending* / introText 均可）
  // payload.introText 优先用于覆盖 store.activeAssistantIntro（由 search/generation_confirm 路径传入）
  // 🔴 P2-2: 添加 isFinalizing 原子性标志，防止并发重复 finalize
  const finalizeAssistantMessage = useCallback(async (payload: Partial<Message> & {
    id?: string;
    type?: Message['type'];
    text?: string;
    introText?: string;
    skipBackendId?: boolean;
  }) => {
    const store = useAgentStore.getState();
    // 🔴 P2-2/F-1: 原子性 isFinalizing 标志（store 级别，组件重挂载仍有效）
    if (store.isFinalizing) {
      console.warn('[消息处理] finalizeAssistantMessage 已在进行中，跳过并发调用');
      return;
    }
    useAgentStore.setState({ isFinalizing: true });

    try {
    // 🔴 F-19: finalize 前先同步清理 activeProcessLogs，避免 finalize 和 reset 之间的渲染窗口
    // 读取当前日志并立即标记为 finalized，防止中间状态渲染
    const currentLogs = [...useAgentStore.getState().activeProcessLogs];
    const finalizedLogs = finalizeProcessLogs(currentLogs);
    // 立即清理 store 中的 activeProcessLogs，防止中间渲染显示 "active" 状态日志
    useAgentStore.setState({ activeProcessLogs: [] });

    // 🔵 #28: ID 解析优先使用 builder.id，pendingBackendMsgIds 作为后备
    // builder 架构下消息 ID 统一在 builder.id 中维护
    const builderId = store.activeReplyBuilder?.id;
    const backendAssistantMsgId = store.pendingBackendMsgIds?.assistant_msg_id;
    // 优先级：skipBackendId → builder.id → pendingBackendMsgIds → payload.id
    const finalId = payload.skipBackendId
      ? payload.id
      : (builderId || backendAssistantMsgId || payload.id);

    // 🔴 v5-#1: 双重 finalize 防护 — 除了 lastFinalizedIdRef，还检查 messages 中是否已存在该 ID
    // lastFinalizedIdRef 在组件重挂载时会丢失，但 messages 中的检查是持久的
    if (finalId && store.messages.some(m => m.id === finalId)) {
      console.warn('[消息处理] 消息已存在，跳过重复 finalize:', finalId);
      return;
    }
    if (lastFinalizedIdRef.current === finalId && finalId) {
      console.warn('[消息处理] 重复finalize，跳过:', finalId);
      return;
    }
    lastFinalizedIdRef.current = finalId ?? null;

    // 构造最终消息：payload 字段作为基础（透传 searchData / generationOptions / pending* 等）
    // 关键字段（id/role/type/text/introText/processLogs）显式覆盖，确保一致性
    const effectiveIntroText = payload.introText ?? store.activeAssistantIntro ?? undefined;
    const { id, role, type, text, introText, processLogs, ...otherPayload } = payload as any;
    const finalMessage: Message = {
      ...otherPayload,
      id: finalId,
      role: 'model',
      type: payload.type || 'text',
      text: payload.text ?? '',
      introText: effectiveIntroText,
      processLogs: finalizedLogs,
    };

    const currentSessionId = store.sessionId;

    // 🔵 v3-#3: 原子操作 — 合并 setMessages + resetStreamingState 为一次 setState
    // 避免两个独立调用之间的渲染间隙导致搜索卡片双重渲染
    useAgentStore.setState((state) => ({
      messages: [...state.messages, finalMessage],
      ...STREAMING_RESET,
    }));
    if (currentSessionId) {
      thinkingSecondsMap.delete(currentSessionId);
    }

    if (currentSessionId && finalId) {
      try {
        // 🔴 v6-#3: 合并 metadata + content + msg_type 为一次 API 调用，避免非原子操作
        const metadataToSave: Record<string, any> = {
          process_logs: finalizedLogs,
          intro_text: effectiveIntroText,
        };
        // P3修复：搜索消息保存 skill_result 到 metadata，确保聊天记录恢复时能正确解析 searchData
        if (payload.type === 'search' && (payload as any).searchData) {
          const searchDataToSave = (payload as any).searchData;
          // P1修复：确保 skill_result 中的 summary 有内容
          // 如果 summary 为空但 text 有内容，用 text 填充 summary
          if (!searchDataToSave.summary && payload.text) {
            searchDataToSave.summary = payload.text;
          }
          metadataToSave.skill_result = JSON.stringify(searchDataToSave);
        }
        if (payload.type === 'generation_confirm') {
          if (payload.pendingUserText) metadataToSave.pending_user_text = payload.pendingUserText;
          if (payload.pendingAttachments) metadataToSave.pending_attachments = payload.pendingAttachments;
          if (payload.pendingReferences) metadataToSave.pending_references = (payload.pendingReferences as any[]).map((ref: any) => {
            const { dataUrl, ...rest } = ref;
            return rest;
          });
          if (payload.pendingRefNodeIds) metadataToSave.pending_ref_node_ids = payload.pendingRefNodeIds;
        }
        const cleanContent = finalMessage.text;
        await AgentService.updateMessageMetadata(finalId, currentSessionId, metadataToSave, {
          content: cleanContent !== undefined ? cleanContent : undefined,
          msg_type: payload.type || 'text',
        });
      } catch (err) {
        console.warn('Failed to save message metadata:', err);
      }
    }

    if (currentSessionId) {
      store.setSelectedHistoryId(currentSessionId);
    }

    // 🔴 P2-2/F-1: 清除 isFinalizing 标志
    } finally {
      useAgentStore.setState({ isFinalizing: false });
    }
  }, []);

  return {
    updateMessage,
    addActiveProcessLog,
    completeLatestActiveProcessLog,
    failLatestActiveProcessLog,
    finalizeAssistantMessage,
  };
}
